import React, { useEffect, useState } from "react";
import api from "../api/client";
import { toast } from "sonner";
import { Edit, Trash2, Plus, X } from "lucide-react";

const EMPTY = {
    name: "", slug: "", description: "", flavor: "classic", pack_size: "100g",
    price: 0, original_price: 0, stock: 100, featured: false,
    images: [], ingredients: [], nutrition: {}, badges: [], popularity: 50,
};

export default function Admin() {
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [tab, setTab] = useState("products");
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const loadAll = async () => {
        const [p, o] = await Promise.all([
            api.get("/products"),
            api.get("/admin/orders").catch(() => ({ data: [] })),
        ]);
        setProducts(p.data);
        setOrders(o.data);
    };

    useEffect(() => { loadAll(); }, []);

    const openCreate = () => {
        setEditing({ ...EMPTY });
        setShowForm(true);
    };
    const openEdit = (p) => {
        setEditing({ ...p });
        setShowForm(true);
    };

    const save = async () => {
        try {
            const payload = {
                ...editing,
                price: parseFloat(editing.price),
                original_price: editing.original_price ? parseFloat(editing.original_price) : null,
                stock: parseInt(editing.stock || 0),
                popularity: parseInt(editing.popularity || 50),
                images: typeof editing.images === "string" ? editing.images.split(",").map(s => s.trim()).filter(Boolean) : editing.images,
                ingredients: typeof editing.ingredients === "string" ? editing.ingredients.split(",").map(s => s.trim()).filter(Boolean) : editing.ingredients,
                badges: typeof editing.badges === "string" ? editing.badges.split(",").map(s => s.trim()).filter(Boolean) : editing.badges,
            };
            if (editing.id) {
                await api.put(`/products/${editing.id}`, payload);
                toast.success("Product updated");
            } else {
                await api.post("/products", payload);
                toast.success("Product created");
            }
            setShowForm(false);
            await loadAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this product?")) return;
        try {
            await api.delete(`/products/${id}`);
            toast.success("Deleted");
            await loadAll();
        } catch {
            toast.error("Delete failed");
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 animate-fade-slide">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
                <div>
                    <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange mb-2">Admin</div>
                    <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter">Control room</h1>
                </div>
                {tab === "products" && (
                    <button onClick={openCreate} data-testid="admin-new-product" className="inline-flex items-center gap-2 bg-brand-orange text-white font-bold px-5 py-3 rounded-xl">
                        <Plus size={16} /> New product
                    </button>
                )}
            </div>

            <div className="flex gap-2 mb-8 border-b border-brand-line">
                {["products", "orders"].map((t) => (
                    <button
                        key={t}
                        onClick={() => setTab(t)}
                        data-testid={`admin-tab-${t}`}
                        className={`px-5 py-3 text-sm font-bold uppercase tracking-wider border-b-2 ${
                            tab === t ? "border-brand-orange text-brand-orange" : "border-transparent text-brand-moss"
                        }`}
                    >
                        {t}
                    </button>
                ))}
            </div>

            {tab === "products" ? (
                <div className="bg-white border border-brand-line rounded-2xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-brand-cream border-b border-brand-line">
                                <tr>
                                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Name</th>
                                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Flavor</th>
                                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Pack</th>
                                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Price</th>
                                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Stock</th>
                                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((p) => (
                                    <tr key={p.id} className="border-b border-brand-line last:border-0">
                                        <td className="px-4 py-3 font-bold">{p.name}</td>
                                        <td className="px-4 py-3">{p.flavor}</td>
                                        <td className="px-4 py-3">{p.pack_size}</td>
                                        <td className="px-4 py-3">₹{p.price}</td>
                                        <td className="px-4 py-3">{p.stock}</td>
                                        <td className="px-4 py-3 flex gap-2">
                                            <button onClick={() => openEdit(p)} data-testid={`admin-edit-${p.slug}`} className="text-brand-ink hover:text-brand-orange"><Edit size={16} /></button>
                                            <button onClick={() => remove(p.id)} data-testid={`admin-delete-${p.slug}`} className="text-destructive"><Trash2 size={16} /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="bg-white border border-brand-line rounded-2xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-brand-cream border-b border-brand-line">
                                <tr>
                                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Order</th>
                                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Email</th>
                                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Total</th>
                                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Status</th>
                                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Payment</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map((o) => (
                                    <tr key={o.id} className="border-b border-brand-line last:border-0">
                                        <td className="px-4 py-3 font-mono text-xs">{o.id.slice(0, 8)}</td>
                                        <td className="px-4 py-3">{o.email}</td>
                                        <td className="px-4 py-3 font-bold">₹{o.totals?.total?.toFixed(2)}</td>
                                        <td className="px-4 py-3">{o.status}</td>
                                        <td className="px-4 py-3">{o.payment_status}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* FORM */}
            {showForm && editing && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-auto">
                    <div className="bg-white rounded-3xl p-6 md:p-8 w-full max-w-2xl max-h-[90vh] overflow-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="font-heading font-black text-2xl">{editing.id ? "Edit" : "New"} product</h3>
                            <button onClick={() => setShowForm(false)}><X /></button>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Name" value={editing.name} onChange={(v) => setEditing({...editing, name: v})} testid="admin-field-name" />
                            <Field label="Slug" value={editing.slug} onChange={(v) => setEditing({...editing, slug: v})} testid="admin-field-slug" />
                            <Field label="Flavor" value={editing.flavor} onChange={(v) => setEditing({...editing, flavor: v})} />
                            <Field label="Pack size" value={editing.pack_size} onChange={(v) => setEditing({...editing, pack_size: v})} />
                            <Field label="Price" value={editing.price} onChange={(v) => setEditing({...editing, price: v})} type="number" />
                            <Field label="Original price" value={editing.original_price || ""} onChange={(v) => setEditing({...editing, original_price: v})} type="number" />
                            <Field label="Stock" value={editing.stock} onChange={(v) => setEditing({...editing, stock: v})} type="number" />
                            <Field label="Popularity" value={editing.popularity} onChange={(v) => setEditing({...editing, popularity: v})} type="number" />
                            <Field label="Images (comma-separated URLs)" value={Array.isArray(editing.images) ? editing.images.join(", ") : editing.images} onChange={(v) => setEditing({...editing, images: v})} cls="sm:col-span-2" />
                            <Field label="Ingredients (comma-separated)" value={Array.isArray(editing.ingredients) ? editing.ingredients.join(", ") : editing.ingredients} onChange={(v) => setEditing({...editing, ingredients: v})} cls="sm:col-span-2" />
                            <Field label="Badges (comma-separated)" value={Array.isArray(editing.badges) ? editing.badges.join(", ") : editing.badges} onChange={(v) => setEditing({...editing, badges: v})} cls="sm:col-span-2" />
                            <div className="sm:col-span-2">
                                <label className="text-xs font-bold text-brand-moss mb-2 uppercase tracking-wider block">Description</label>
                                <textarea
                                    value={editing.description}
                                    onChange={(e) => setEditing({...editing, description: e.target.value})}
                                    className="w-full bg-brand-cream border-none rounded-xl px-4 py-3 min-h-[80px] focus:outline-none focus:ring-2 focus:ring-brand-orange"
                                />
                            </div>
                            <label className="flex items-center gap-2 sm:col-span-2">
                                <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({...editing, featured: e.target.checked})} />
                                <span className="font-bold text-sm">Featured</span>
                            </label>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button onClick={save} data-testid="admin-save" className="bg-brand-orange text-white font-bold px-6 py-3 rounded-xl">Save</button>
                            <button onClick={() => setShowForm(false)} className="border-2 border-brand-ink font-bold px-6 py-3 rounded-xl">Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, value, onChange, type = "text", cls = "", testid }) {
    return (
        <div className={cls}>
            <label className="text-xs font-bold text-brand-moss mb-2 uppercase tracking-wider block">{label}</label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="w-full bg-brand-cream border-none rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-orange"
            />
        </div>
    );
}
